import { useEffect, useRef, useState } from 'react';
//import PropTypes from 'prop-types';
import fetch from 'cross-fetch';
import Geocode from 'react-geocode';
import { usePlacesWidget } from 'react-google-autocomplete';
import Select from 'react-select';
import clsx from 'clsx';

import Loader from '../loader';
import StyledModal from '../styledModal';
import GoogleMap from './googleMap';
import InfoWindow from './infoWindow';
import Cluster from './cluster';
import Marker from './marker';
import useSupercluster from 'use-supercluster';
import Location from './location';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import {
	DropdownIndicator,
	ClearIndicator,
	selectStyles,
	selectStylesMob,
} from '../../utils/select';
import Close from '../../images/close.inline.svg';
import { BLOCKS } from '@contentful/rich-text-types';

const options = {
	renderNode: {
		[BLOCKS.PARAGRAPH]: (node, children) => (
			<p className="typography typography--body-big">{children}</p>
		),
	},
};

const LocationSearch = ({
	errorTitle,
	errorMessage,
	defaultMapCenter,
	radiusOptions,
	defaultRadiusFilter,
	bookingCta,
	defaultZoom,
	getUserLocation,
}) => {
	const radiusOptionsMob = radiusOptions.options.map((opt) => {
		const { value } = opt;
		const label = opt.label.split('Less than ')[1];
		return { value, label };
	});
	const mapRef = useRef({
		search: [defaultMapCenter.lat, defaultMapCenter.lon],
		radius: defaultRadiusFilter,
		mapsObj: null,
		map: null,
		places: [],
		searchedPlaces: [],
	});
	let { current } = mapRef;
	const radiusSelect = useRef();
	const [isLoading, setIsLoading] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [modalIsOpen, setIsOpen] = useState(false);
	const [showList, setShowList] = useState(false);
	const [activePlace, setActivePlace] = useState(null);
	const [bounds, setBounds] = useState(null);
	const [zoom, setZoom] = useState(defaultZoom);
	const [points, setPoints] = useState([]);
	const [showMapMobile, setShowMapMobile] = useState(true);

	// autocomplete hook
	const { ref: locationInput } = usePlacesWidget({
		apiKey: process.env.GATSBY_GOOGLE_MAPS_KEY,
		options: {
			types: 'address',
		},
		onPlaceSelected: (place) => {
			console.log(place);
		},
	});

	const { clusters, supercluster } = useSupercluster({
		points,
		bounds,
		zoom,
		options: { radius: 75, maxZoom: 20 },
	});

	useEffect(() => {
		render();
	}, [current.search, current.radius]);

	useEffect(async () => {
		// Set API key for googs
		await Geocode.setApiKey(process.env.GATSBY_GOOGLE_MAPS_KEY);
		await fetchData();
		userLocation();
	}, []);

	// Grab user's location and get as center
	const successCallback = async (pos) => {
		console.log('permission granted');
		current.search = [pos.coords.latitude, pos.coords.longitude];
		const address = await Geocode.fromLatLng(
			pos.coords.latitude,
			pos.coords.longitude
		);
		if (locationInput.current) {
			locationInput.current.value = address.results[0].formatted_address;
		}
		current.radius = radiusSelect.current.props.value;
		handleSearch();
		setIsLoading(false);
	};

	const errorCallback = (error) => {
		if (error.code == error.PERMISSION_DENIED) {
			console.log('permission denied');
			current.search = [defaultMapCenter.lat, defaultMapCenter.lon];
			current.radius = radiusSelect.current.props.value;
		}
		setIsLoading(false);
	};

	const userLocation = () => {
		setIsLoading(true);
		if (getUserLocation) {
			navigator.geolocation.getCurrentPosition(successCallback, errorCallback);
		} else {
			successCallback({
				coords: {
					latitude: defaultMapCenter.lat,
					longitude: defaultMapCenter.lon,
				},
			});
		}
	};

	// fetch from magento and set state
	const fetchData = async () => {
		setIsLoading(true);
		try {
			const response = await fetch(process.env.GATSBY_MAGENTO_PROD_ENDPOINT);
			const results = await response.json();
			current.places = results;
			current.searchedPlaces = results;
			setPoints(
				results.map((crime) => ({
					type: 'Feature',
					properties: {
						cluster: false,
						crimeId: crime.id,
					},
					geometry: {
						type: 'Point',
						coordinates: [parseFloat(crime.lng), parseFloat(crime.lat)],
					},
				}))
			);
			//setIsLoading(false);
		} catch (error) {
			//setIsLoading(false);
			setHasError(true);
			console.error(error);
		}
	};

	const toggleModal = () => setIsOpen(!modalIsOpen);

	const sorted = () => {
		// user search

		const searchRes = new current.mapsObj.LatLng(
			current.search[0],
			current.search[1]
		);
		// sort em based on distance
		const sortedPlaces = current.places.slice().sort((a, b) => {
			const aMap = new current.mapsObj.LatLng(a.lat, a.lng);
			const bMap = new current.mapsObj.LatLng(b.lat, b.lng);
			const aDiff = current.mapsObj.geometry.spherical.computeDistanceBetween(
				aMap,
				searchRes
			);
			const bDiff = current.mapsObj.geometry.spherical.computeDistanceBetween(
				bMap,
				searchRes
			);
			return aDiff - bDiff;
		});

		// filter based on radius
		const filtered = sortedPlaces.filter((place) => {
			const placeLoc = new current.mapsObj.LatLng(place.lat, place.lng);
			// convert to miles for murica
			const distance =
				current.mapsObj.geometry.spherical.computeDistanceBetween(
					searchRes,
					placeLoc
				) * 0.000621371192;
			return parseFloat(distance) < parseFloat(current.radius) ? place : '';
		});

		// reduce to max results
		// const max =
		//   filtered.length > results ? filtered.slice(0, results) : filtered;
		if (filtered.length > 0) {
			current.searchedPlaces = filtered;
			current.searchedPlaces[0].show = true;
			setActivePlace(current.searchedPlaces[0]);
			setIsLoading(false);
		} else {
			current.search = [defaultMapCenter.lat, defaultMapCenter.lon];
			current.radius = radiusSelect.current.props.value;
			current.map.setZoom(defaultZoom);
			setActivePlace(null);
			toggleModal();
			setIsLoading(false);
			return false;
		}

		// fit in bounds
		const bounds = new current.mapsObj.LatLngBounds();
		bounds.extend(
			new current.mapsObj.LatLng(
				current.searchedPlaces[0].lat,
				current.searchedPlaces[0].lng
			)
		);
		bounds.extend(
			new current.mapsObj.LatLng(
				current.searchedPlaces[current.searchedPlaces.length - 1].lat,
				current.searchedPlaces[current.searchedPlaces.length - 1].lng
			)
		);
		current.map.fitBounds(bounds);
	};

	const handleKeydown = (e) => {
		if (e.key === 'Enter') {
			handleSearch();
		}
	};

	const handleSearch = async () => {
		setIsLoading(true);
		// check fields are not empty
		if (
			locationInput.current.value === '' ||
			radiusSelect.current.props.value === null
		) {
			setIsLoading(false);
			toggleModal();
			return false;
		}

		const location = locationInput.current.value;

		// const userRadius =
		// 	unit === 'mi'
		// 		? radiusSelect.current.props.value.value
		// 		: radiusSelect.current.props.value.value * 1.609;
		try {
			const response = await Geocode.fromAddress(location);
			const { lat, lng } = response.results[0].geometry.location;
			current.search = [lat, lng];
			current.radius = radiusSelect.current.props.value.value;
			sorted();
			setShowList(true);
		} catch (error) {
			console.error(error);
		}
	};

	// onChildClick callback can take two arguments: key and childProps
	const onChildClickCallback = (key) => {
		const tmpPlaces = current.places.slice();
		const index = tmpPlaces.findIndex((place) => place.id == key);
		if (tmpPlaces[index]?.show === true) {
			tmpPlaces.forEach((place) => (place.show = false));
			tmpPlaces[index].show === false;
			setActivePlace(null);
		} else {
			tmpPlaces.forEach((place) => (place.show = false));
			if (tmpPlaces[index]) {
				tmpPlaces[index].show = true;
				setActivePlace(tmpPlaces[index]);
			}
		}
		current.places = tmpPlaces;
	};

	const handleApiLoaded = (map, maps) => {
		current.mapsObj = maps;
		current.map = map;
	};

	const handleClusterExpansion = (id, latitude, longitude) => {
		const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(id), 20);
		current.map.setZoom(expansionZoom);
		current.map.panTo({
			lat: latitude,
			lng: longitude,
		});
		if (activePlace) {
			const tempPlace = Object.assign({}, activePlace, {
				show: true,
			});
			setActivePlace(tempPlace);
		}
	};

	const handleOnMapChange = (zoom, bounds) => {
		setZoom(zoom);
		setBounds([bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat]);
	};

	const render = () => {
		return (
			<>
				<section className="search-controls" id="map-location">
					<section className="search-controls__wrap">
						<label className="hidden" htmlFor="location">
							Location
						</label>
						<input
							className="input search-controls__input"
							placeholder={'Enter location'}
							onKeyDown={handleKeydown}
							ref={locationInput}
							id="location"
						/>
						<Select
							id="radius"
							name="radius"
							classNamePrefix="select"
							placeholder="Select Distance Radius"
							options={radiusOptions.options}
							ref={radiusSelect}
							components={{ DropdownIndicator, ClearIndicator }}
							styles={selectStyles}
							isClearable={true}
							isSearchable={false}
							defaultValue={radiusOptions.options.find(
								(option) => option.value == defaultRadiusFilter
							)}
						/>
						<Select
							id="radius-mob"
							name="radius"
							classNamePrefix="select"
							placeholder="Select Distance Radius"
							options={radiusOptionsMob}
							ref={radiusSelect}
							components={{ DropdownIndicator, ClearIndicator }}
							styles={selectStylesMob}
							isClearable={true}
							isSearchable={false}
							defaultValue={radiusOptionsMob.find(
								(option) => option.value == defaultRadiusFilter
							)}
						/>
						{/* <Select className="category" ref={category} /> */}
						<button
							className="btn btn--secondary search-controls__submit search-controls__submit--desk"
							onClick={() => handleSearch()}
						>
							Search
						</button>
						<button
							className="btn btn--secondary search-controls__submit search-controls__submit--mob"
							onClick={() => handleSearch()}
						>
							Search
						</button>
					</section>
					<section className="search-controls__tabs">
						<button
							className={clsx(
								'btn btn--secondary search-controls__tab',
								showMapMobile && 'search-controls__tab--active'
							)}
							onClick={() => setShowMapMobile(true)}
						>
							Map View
						</button>
						<button
							className={clsx(
								'btn btn--secondary search-controls__tab',
								!showMapMobile && 'search-controls__tab--active'
							)}
							onClick={() => setShowMapMobile(false)}
						>
							List View
						</button>
					</section>
					<div
						className={`location-results ${
							showMapMobile ? 'location-results--show-mobile' : ''
						}`}
					>
						<section
							className={`location-search ${
								showMapMobile ? 'location-search--show-mobile' : ''
							}`}
						>
							<Loader isLoading={isLoading} />
							{hasError === false && (
								<>
									<GoogleMap
										defaultZoom={zoom}
										defaultCenter={[
											defaultMapCenter.lat,
											defaultMapCenter.lon,
										]}
										center={current.search}
										bootstrapURLKeys={{
											key: process.env.GATSBY_GOOGLE_MAPS_KEY,
											libraries: ['geometry', 'places'],
										}}
										onChildClick={onChildClickCallback}
										yesIWantToUseGoogleMapApiInternals={true}
										onGoogleApiLoaded={({ map, maps }) =>
											handleApiLoaded(map, maps)
										}
										onChange={({ zoom, bounds }) =>
											handleOnMapChange(zoom, bounds)
										}
									>
										{clusters.map((cluster) => {
											const [longitude, latitude] =
												cluster.geometry.coordinates;
											const {
												cluster: isCluster,
												point_count: pointCount,
											} = cluster.properties;

											if (isCluster) {
												return (
													<Cluster
														key={cluster.id}
														lat={latitude}
														lng={longitude}
														points={points}
														pointCount={pointCount}
														onClick={() =>
															handleClusterExpansion(
																cluster.id,
																latitude,
																longitude
															)
														}
													/>
												);
											} else {
												return (
													<Marker
														key={cluster.properties.crimeId}
														lat={latitude}
														lng={longitude}
														show={
															cluster?.properties
																?.crimeId ===
															activePlace?.id
														}
													/>
												);
											}
										})}
									</GoogleMap>
									<InfoWindow place={activePlace} />
								</>
							)}
						</section>
						{isLoading === false && hasError === false && (
							<section
								className={clsx(
									'location-listing',
									showMapMobile && 'location-listing--show-mobile',
									showList && 'is-active'
								)}
							>
								<div className="location-listing__wrap">
									{current.searchedPlaces.map((place) => (
										<Location
											key={place.id}
											place={place}
											cta={bookingCta}
										/>
									))}
								</div>
							</section>
						)}
					</div>
				</section>
				<StyledModal
					modalIsOpen={modalIsOpen}
					closeModal={() => toggleModal()}
					active={true}
				>
					<section className="styled-modal__content">
						<header className="styled-modal__header">
							<h2 className="typography typography--h2">{errorTitle}</h2>
							<button
								className="btn btn--close"
								onClick={() => toggleModal()}
							>
								<Close />
							</button>
						</header>
						<div className="styled-modal__body">
							{renderRichText(errorMessage, options)}

							<button
								className="btn btn--secondary"
								onClick={() => toggleModal()}
							>
								Close
							</button>
						</div>
					</section>
				</StyledModal>
			</>
		);
	};

	return render();
};

export default LocationSearch;
